import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import UserAvatar from "../components/UserAvatar";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Loader2, Star, ShoppingBag,
  Users, Package, TrendingUp
} from "lucide-react";
import api from "../lib/api";

const SORT_OPTIONS = [
  { id: "popular", label: "Most Popular" },
  { id: "most_items", label: "Most Items" },
  { id: "highest_rated", label: "Highest Rated" },
];

export default function CreatorsDirectoryPage() {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("popular");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ sort });
    if (searchQuery) params.set("q", searchQuery);
    api.get(`/marketplace/creators?${params}`)
      .then((r) => setCreators(r.data.creators))
      .catch(() => toast.error("Failed to load creators"))
      .finally(() => setLoading(false));
  }, [sort, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  return (
    <div className="min-h-screen" data-testid="creators-directory-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 nav-glass px-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3 h-12">
          <button onClick={() => navigate("/marketplace")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer" data-testid="creators-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Users className="w-4 h-4 text-cyan-400" />
          <h1 className="font-outfit text-sm font-semibold text-foreground mr-4">Creators</h1>
          <form onSubmit={handleSearch} className="flex-1 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
            <input data-testid="creators-search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search creators..." className="w-full pl-9 pr-4 py-1.5 bg-muted/40 border border-border rounded-full text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25" />
          </form>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Sort */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-xs text-muted-foreground">{creators.length} creators</p>
          <div className="flex gap-1">
            {SORT_OPTIONS.map((s) => (
              <button key={s.id} onClick={() => setSort(s.id)} className={`px-2.5 py-1 rounded text-[11px] font-medium border-0 cursor-pointer ${sort === s.id ? "bg-cyan-500/10 text-cyan-400" : "bg-transparent text-muted-foreground/70 hover:text-muted-foreground"}`} data-testid={`sort-${s.id}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
        ) : creators.length === 0 ? (
          <div className="text-center py-16"><Users className="w-8 h-8 text-muted-foreground/60 mx-auto mb-3" /><p className="text-sm text-muted-foreground">No creators found</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="creators-grid">
            {creators.map((c) => (
              <Link to={`/creators/${c.username}`} key={c.username} className="bg-card border border-border/60 rounded-xl overflow-hidden no-underline card-hover group" data-testid={`creator-card-${c.username}`}>
                {/* Gradient top bar */}
                <div className="h-16 bg-gradient-to-r from-cyan-300 via-purple-400 to-pink-400 relative">
                  <div className="absolute -bottom-5 left-4">
                    <div className="neon-ring rounded-full">
                      <UserAvatar name={c.display_name} username={c.username} avatarUrl={c.avatar_url} size="md" />
                    </div>
                  </div>
                </div>
                <div className="pt-8 p-4">
                  <h3 className="text-sm font-semibold text-foreground">{c.display_name}</h3>
                  <p className="text-xs text-muted-foreground/70 mb-2">@{c.username}</p>
                  {c.creator_bio && <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{c.creator_bio}</p>}
                  <div className="flex items-center gap-4 text-[11px] text-muted-foreground/70">
                    <span className="flex items-center gap-1"><Package className="w-3 h-3" /> {c.item_count} items</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {c.subscriber_count}</span>
                    {c.avg_rating > 0 && <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400 fill-amber-400" /> {c.avg_rating}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
